import {
  OreIcon,
  OrePill,
  OreStack,
  OreTable,
  OreTableBody,
  OreTableCaption,
  OreTableCell,
  OreTableContainer,
  OreTableHead,
  OreTableRow,
  OreText,
  OreTooltip,
  SvgInfo,
} from '@edenredespana/oreneta'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { OrePillTones } from '@/AccessRoles/domain/PillTones'
import { AutoEnrollmentDefaultConditionsSubProductModel } from '@/AutoEnrollment/domain'
import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import { contractTranslation } from '@/Contract/ui/contractTranslation'
import { edenredProducts } from '@/Shared/domain'
import { navigationRoutes } from '@/Shared/ui/routers'

// La misma tabla se reutiliza en contratos y autoenrollment. En contratos puede llegar `isNew`.
type Step3SubProductModel = AutoEnrollmentDefaultConditionsSubProductModel & {
  isNew?: boolean
}

interface Step3ServicesTableProps {
  parentProductCode?: edenredProducts
  subProducts: Step3SubProductModel[]
  amendmentStartDate?: string
  disabledState: boolean
  showServiceTooltip?: boolean
  // Prop añadida: permite sobreescribir el título de la tabla (por defecto usa la clave i18n de AutoEnrollment)
  title?: string
  subProductsToggleChange?: (
    productCode: edenredProducts,
    subProductServiceCode: number,
  ) => void
  subProductsToggleState?: boolean[]
}

export const Step3SubProductsTable = ({
  subProducts,
  amendmentStartDate,
  disabledState,
  showServiceTooltip = false,
  title,
}: Step3ServicesTableProps): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()

  // En la ruta de contratacion de nuevos productos esta tabla cambia de formato
  // para mostrarse con tres columnas: servicio, descripcion y participacion.
  const isContractNewProductsRoute =
    location.pathname === navigationRoutes.contractNewProducts
  const isAutoEnrollmentRoute = location.pathname === navigationRoutes.autoEnrollment

  // Cada subproducto premium tiene un texto fijo para el tooltip o para la columna
  // de descripcion en la vista especifica de contratacion.
  const serviceDescriptionTextBySubProductId: Record<number, string> = {
    44: t(contractTranslation.productsData.products.tooltipText.santanderText),
    45: t(contractTranslation.productsData.products.tooltipText.iberiaText),
    51: t(contractTranslation.productsData.products.tooltipText.mapfreText),
  }

  const getTooltip = (subProductId: number) => {
    switch (subProductId) {
      case 44:
        return (
          <OreText size="text-sm" tone="white">
            {t(contractTranslation.productsData.products.tooltipText.santanderText)}
          </OreText>
        )
      case 45:
        return (
          <OreText size="text-sm" tone="white">
            {t(contractTranslation.productsData.products.tooltipText.iberiaText)}
          </OreText>
        )
      case 51:
        return (
          <OreText size="text-xs" tone="white">
            <Trans>
              {t(contractTranslation.productsData.products.tooltipText.mapfreText)}
            </Trans>
          </OreText>
        )
      default:
        return null
    }
  }
  // El pill solo se muestra cuando el subproducto es nuevo y la fecha de la enmienda
  // está dentro del último mes respecto a la fecha actual.
  const canShowNewPill = (isNew?: boolean): boolean => {
    if (!isNew || !amendmentStartDate) {
      return false
    }

    const parsedAmendmentStartDate = new Date(amendmentStartDate)

    if (Number.isNaN(parsedAmendmentStartDate.getTime())) {
      return false
    }

    const currentDate = new Date()
    // Calculamos la fecha límite inferior: exactamente hace un mes desde hoy.
    let oneMonthAgo = new Date(currentDate)
    oneMonthAgo.setMonth(currentDate.getMonth() - 1)

    // Solo mostramos el pill si la enmienda está entre hace un mes y la fecha actual.
    // Si la fecha es más antigua, como 2024-05-07, no debe mostrarse.
    return (
      parsedAmendmentStartDate >= oneMonthAgo && parsedAmendmentStartDate <= currentDate
    )
  }

  const getServiceDescriptionText = (subProductId: number): string | undefined =>
    serviceDescriptionTextBySubProductId[subProductId]

  return (
    <OreTableContainer>
      <OreTable>
        {!isContractNewProductsRoute && !isAutoEnrollmentRoute ? (
          <OreTableCaption
            // Si se pasa title se usa el valor externo; si no, se usa el texto por defecto de AutoEnrollment
            title={
              title ??
              t(AutoEnrollmentTranslation.productsConditions.subProductsTable.title)
            }
            disabled={disabledState}
          />
        ) : null}
        {/* Cabecera exclusiva para la pantalla de nuevos productos, con el layout de la maqueta. */}
        {isContractNewProductsRoute || isAutoEnrollmentRoute ? (
          <OreTableHead valign="middle">
            <OreTableRow hover={false}>
              <OreTableCell as="th">
                <span style={{ whiteSpace: 'pre-line', display: 'flex' }}>
                  {t(AutoEnrollmentTranslation.titleServices)}
                </span>
              </OreTableCell>
              <OreTableCell as="th">
                <span style={{ whiteSpace: 'pre-line', display: 'flex' }}>
                  {t(AutoEnrollmentTranslation.titleDescription)}
                </span>
              </OreTableCell>
              <OreTableCell as="th" align="center">
                <span
                  style={{
                    whiteSpace: 'pre-line',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    width: '100%',
                  }}
                >
                  {t(AutoEnrollmentTranslation.titleParticipation)}
                  <OreTooltip
                    text={t(
                      contractTranslation.productsData.products.tooltipText
                        .participationText,
                    )}
                    placement="top"
                    separation="small"
                  >
                    <OreIcon tone="neutral" size="extra-small" icon={<SvgInfo />} />
                  </OreTooltip>
                </span>
              </OreTableCell>
            </OreTableRow>
          </OreTableHead>
        ) : null}
        <OreTableBody>
          {subProducts?.length
            ? subProducts.map((subProduct, i) =>
                (() => {
                  const serviceTooltipText = getTooltip(subProduct.id)

                  // En contratacion mostramos una fila tabular clasica con la descripcion
                  // separada del porcentaje de participacion.
                  if (isContractNewProductsRoute || isAutoEnrollmentRoute) {
                    return (
                      <OreTableRow key={`sub-product-row-${i}`}>
                        <OreTableCell size="quarter" align="left">
                          <OreText
                            align="left"
                            bold
                            tone={disabledState ? 'neutral-500' : 'neutral-600'}
                          >
                            {subProduct.name}
                          </OreText>
                        </OreTableCell>
                        <OreTableCell align="left">
                          <OreText
                            align="left"
                            tone={disabledState ? 'neutral-500' : 'neutral-600'}
                          >
                            <Trans t={t}>
                              {getServiceDescriptionText(subProduct.id) ?? ''}
                            </Trans>
                          </OreText>
                        </OreTableCell>
                        <OreTableCell size="quarter" align="center">
                          {subProduct.services?.map((condition, j) => (
                            <OreText
                              key={`sub-product-participation-${j}`}
                              align="center"
                              tone={disabledState ? 'neutral-500' : 'neutral-600'}
                            >
                              {condition.value}
                            </OreText>
                          ))}
                        </OreTableCell>
                      </OreTableRow>
                    )
                  }

                  // En el resto de pantallas mantenemos la tabla reutilizable original,
                  // con el detalle del servicio debajo del nombre del subproducto.
                  return (
                    <OreTableRow key={`sub-product-row-${i}`}>
                      <OreTableCell align="left">
                        <OreText
                          align="left"
                          bold
                          tone={disabledState ? 'neutral-500' : 'neutral-600'}
                        >
                          {subProduct.name}
                        </OreText>
                        {subProduct.services?.length
                          ? subProduct.services.map((condition, j) => (
                              <OreText
                                key={`sub-product-condition-${j}`}
                                align="left"
                                tone={disabledState ? 'neutral-500' : 'neutral-600'}
                              >
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                  }}
                                >
                                  <span>{`${condition.name}: ${condition.value}`}</span>
                                  {showServiceTooltip && serviceTooltipText ? (
                                    <OreTooltip
                                      text={serviceTooltipText}
                                      placement="right"
                                      separation="small"
                                    >
                                      <span style={{ display: 'inline-flex' }}>
                                        <OreIcon
                                          tone="neutral"
                                          size="2-extra-small"
                                          icon={<SvgInfo />}
                                        />
                                      </span>
                                    </OreTooltip>
                                  ) : null}
                                </span>
                              </OreText>
                            ))
                          : null}
                      </OreTableCell>
                      {canShowNewPill(subProduct.isNew) ? (
                        <OreTableCell align="right">
                          <OreStack placeContent="end">
                            <OrePill tone={OrePillTones.gray}>
                              {t(contractTranslation.productsData.products.pill.title)}
                            </OrePill>
                          </OreStack>
                        </OreTableCell>
                      ) : null}
                    </OreTableRow>
                  )
                })(),
              )
            : null}
        </OreTableBody>
      </OreTable>
    </OreTableContainer>
  )
}
